import React, { useEffect, useMemo, useState } from "react";
import "../../styles/checkout.css";
import {
  Card,
  Row,
  Col,
  Typography,
  Input,
  Button,
  Divider,
  Tag,
  message,
  Radio,
  Spin,
} from "antd";
import {
  EnvironmentOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  FaShoppingBag,
  FaMoneyBillWave,
  FaMapMarkerAlt,
  FaMotorcycle,
  FaBalanceScale,
  FaRulerCombined,
  FaRoute,
  FaTruckLoading,
} from "react-icons/fa";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import { useCart } from "../../services/CartContext";
import { useMutation } from "@tanstack/react-query";
import { addOrder } from "../../services/OrderService";
import { useValidateCart } from "../../hooks/useCartItems";
import { v4 as uuidv4 } from "uuid";

import SuccessModal from "../successModal/SuccessModal";
import LocationField from "../../utility/LocationField";
import TextArea from "antd/es/input/TextArea";
import { useCalculateDeliveryCost } from "../../hooks/useFulfillment";

const { Title, Text } = Typography;

const CUSTOMER_CACHE_KEY = "checkout_customer_cache";
const DELIVERY_THRESHOLD = 3000;
const FIXED_DELIVERY_FEE = 150;

const Checkout = () => {
  const { cart, dispatch } = useCart();
  const cartItems = useMemo(() => cart?.items ?? [], [cart?.items]);

  const campaignCode = localStorage.getItem("campaignCode");

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [agentCode, setAgentCode] = useState("");
  const [address, setAddress] = useState("");
  const [street, setStreet] = useState("");
  const [notes, setNotes] = useState("");

  const [coordinates, setCoordinates] = useState({ lat: null, lng: null });
  const [isPinning, setIsPinning] = useState(false);
  
  // Restricted options: 'delivery' or 'self_collect' (express is silenced)
  const [deliveryOption, setDeliveryOption] = useState("delivery");

  const [showSuccess, setShowSuccess] = useState(false);
  const [successCart, setSuccessCart] = useState(null);

  const [idempotencyKey] = useState(() => uuidv4());
  const [phoneError, setPhoneError] = useState("");

  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.displayPrice * item.qty, 0),
    [cartItems]
  );

  // Determine if the order qualifies for free delivery tier
  const isFreeTier = subtotal >= DELIVERY_THRESHOLD;

  // Maps to backend DeliveryCalculationItemDto format
  const deliveryCalculationItems = useMemo(() => {
    return cartItems.map((item) => ({
      productId: item.productId,
      qty: item.qty * (item.conversionRate || 1),
    }));
  }, [cartItems]);

  // Execute distance computation hook only if we are in the free tier tier (>= 3000) for cost auditing
  const { data: deliveryData, isLoading: isCalculatingDelivery } =
    useCalculateDeliveryCost(
      {
        campaignCode,
        latitude: coordinates.lat || 0,
        longitude: coordinates.lng || 0,
        deliveryType: "delivery",
        isSelfCollect: false,
        isExpress: false,
        items: deliveryCalculationItems,
      },
      {
        enabled: isFreeTier && deliveryOption !== "self_collect" && coordinates.lat !== null && coordinates.lng !== null,
        retry: false,
        onError: (err) => {
          message.error(err?.response?.data || "Failed to compute distance profile");
        }
      }
    );

  // Customer-facing delivery fee rule mapping
  const deliveryFee = useMemo(() => {
    if (deliveryOption === "self_collect") return 0;
    if (isFreeTier) return 0; // Free delivery activated for customer
    return FIXED_DELIVERY_FEE; // Fixed tier under 3000
  }, [deliveryOption, isFreeTier]);

  const total = subtotal + deliveryFee;
  const validateCart = useValidateCart();

  const placeOrder = useMutation({
    mutationFn: ({ payload, idempotencyKey }) =>
      addOrder(payload, idempotencyKey),
  });

  const handlePinLocation = () => {
    if (!navigator.geolocation) {
      message.error("Geolocation is not supported");
      return;
    }

    setIsPinning(true);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setCoordinates({ lat: latitude, lng: longitude });
        setStreet(`Lat: ${latitude.toFixed(5)}, Lng: ${longitude.toFixed(5)}`);
        setIsPinning(false);
        message.success("Coordinates captured successfully");
      },
      () => {
        setIsPinning(false);
        message.error("Could not determine position automatically.");
      }
    );
  };

  const handlePlaceOrder = async () => {
    try {
      if (!name.trim()) return message.error("Customer name required");
      if (!phone.trim()) return message.error("Phone number required");
      if (!address.trim()) return message.error("Physical address designation required");
      
      if (deliveryOption !== "self_collect" && (coordinates.lat === null || coordinates.lng === null)) {
        return message.error("Please lock your pinpoint location down to resolve delivery paths");
      }

      const validation = await validateCart.mutateAsync(cartItems);
      if (!validation.isValid) {
        validation.invalidItems.forEach((i) =>
          message.error(`${i.productName} has insufficient warehouse stock`)
        );
        return;
      }

      // Maps perfectly to modified C# backend configurations including audit matrices
      const payload = {
        campaignCode,
        outletPhone: phone.replace(/^\+/, ""),
        userName: name,
        address,
        latitude: coordinates.lat || 0,
        longitude: coordinates.lng || 0,
        deliveryFee: deliveryFee, // Customer billing cost (0 or 150)
        isSelfCollect: deliveryOption === "self_collect",
        isExpress: false, 
        orderInstructions: notes,
        agentCode,
        priority: 0,
        isDiscounted: isFreeTier && deliveryOption !== "self_collect", // Track discount status
        
        // Pass system logistics tracking parameters up for internal evaluations
        deliveryDistanceKm: deliveryOption === "self_collect" ? 0 : (isFreeTier ? (deliveryData?.distanceKm || 0) : 0),
        deliveryWeightKg: deliveryOption === "self_collect" ? 0 : (isFreeTier ? (deliveryData?.totalWeightKg || 0) : 0),
        deliveryVolumeM3: deliveryOption === "self_collect" ? 0 : (isFreeTier ? (deliveryData?.totalVolumeM3 || 0) : 0),
        deliveryCost: deliveryOption === "self_collect" ? 0 : (isFreeTier ? (deliveryData?.subTotal || 0) : FIXED_DELIVERY_FEE), 
        vehicleTypeId: deliveryOption === "self_collect" ? null : (isFreeTier ? (deliveryData?.vehicleId || null) : null),

        orderItems: cartItems.map((item) => ({
          productId: item.productId,
          qty: item.qty * (item.conversionRate || 1),
          unit_price: item.piecePrice,
          DisplayUnitName: item.displayUnitName,
          DisplayConversionRate: item.conversionRate || 1,
          DisplayUnitPrice: item.displayPrice,
        })),
      };

      const snapshot = { items: [...cartItems] };
      await placeOrder.mutateAsync({ payload, idempotencyKey });

      dispatch({ type: "CLEAR_CART" });
      setSuccessCart(snapshot);
      setShowSuccess(true);

      localStorage.setItem(
        CUSTOMER_CACHE_KEY,
        JSON.stringify({ name, phone, address, agentCode, notes })
      );

      message.success("Order dispatched successfully!");
    } catch (e) {
      message.error("An error occurred during transaction processing.");
    }
  };

  useEffect(() => {
    const cached = localStorage.getItem(CUSTOMER_CACHE_KEY);
    if (cached) {
      try {
        const data = JSON.parse(cached);
        setName(data.name || "");
        setPhone(data.phone || "");
        setAddress(data.address || "");
        setAgentCode(data.agentCode || "");
        setNotes(data.notes || "");
      } catch (e) {
        console.error("Invalid cached profile credentials");
      }
    }
  }, []);

const handlePhoneChange = (e) => {
  // Extract string value if e is an event or raw string input
  let value = typeof e === "string" ? e : e.target.value;

  // 1. Remove all spaces and special non-digit characters (keep leading + if present temporarily)
  let sanitized = value.replace(/\s+/g, "").replace(/[^\d+]/g, "");

  // 2. Normalize leading '+' or leading local '0' to standard '254'
  if (sanitized.startsWith("+254")) {
    sanitized = sanitized.slice(1);
  } else if (sanitized.startsWith("0")) {
    sanitized = "254" + sanitized.slice(1);
  }

  // 3. Fix the common user error: 25407... or 25401... -> 2547... or 2541...
  if (/^2540[17]/.test(sanitized)) {
    sanitized = "254" + sanitized.slice(4);
  }

  // Update input field state with the auto-cleaned value
  setPhone(sanitized);

  // 4. Strict Validation: Must start with 2547 or 2541 followed by exactly 7 digits ( total 12 digits )
  const kenyaRegex = /^254(7|1)\d{8}$/;

  if (!sanitized) {
    setPhoneError("");
  } else if (!/^254(7|1)/.test(sanitized)) {
    setPhoneError("Phone must start with a valid prefix (2547... or 2541...)");
  } else if (sanitized.length > 12) {
    setPhoneError("Phone number is too long");
  } else if (!kenyaRegex.test(sanitized)) {
    setPhoneError("Enter a complete 12-digit phone number (e.g., 254712345678)");
  } else {
    setPhoneError("");
  }
};
  return (
    <>
      <div className="modern-checkout">
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={15}>
            
            {/* 1. UPSTREAM ROUTING AND LOCATION DISCOVERY */}
            <Card
              title={
                <div className="checkout-card-title">
                  <FaMapMarkerAlt style={{ color: "#ff4d4f" }} />
                  <span>1. Setup Delivery Point Location</span>
                </div>
              }
              className="checkout-card"
            >
              <div className="checkout-form-grid">
                <Text type="secondary">
                  Please specify your location coordinates early to instantly look up matching fleet rates.
                </Text>

                <LocationField
                  street={street}
                  setStreet={setStreet}
                  setCoordinates={setCoordinates}
                  handlePinLocation={handlePinLocation}
                  isPinning={isPinning}
                />

                <Input
                  size="large"
                  prefix={<EnvironmentOutlined />}
                  placeholder="Apartment name, Floor unit, Street name description..."
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>
            </Card>

            {/* 2. CORE FULFILLMENT SPEED SELECTION FRAMEWORK */}
            <Card
              title={
                <div className="checkout-card-title">
                  <FaMotorcycle style={{ color: "#1677ff" }} />
                  <span>2. Delivery Speed Service Option</span>
                </div>
              }
              className="checkout-card"
              style={{ marginTop: "1rem" }}
            >
              <Radio.Group
                value={deliveryOption}
                onChange={(e) => setDeliveryOption(e.target.value)}
                className="delivery-radio-group"
              >
                {/* METHOD A: STANDARD PAID / FREE DELIVERY */}
                <div className={`delivery-option ${deliveryOption === "delivery" ? "active" : ""} ${isFreeTier ? "free-tier-activated" : ""}`}>
                  <Radio value="delivery">
                    <div className="delivery-option-content">
                      <div>
                        <div className="option-title-row">
                          <FaTruckLoading style={{ color: "#1677ff", marginRight: 6 }} />
                          <h4>Standard Consolidated Delivery</h4>
                        </div>
                        <p>Aggregated delivery mode; standard logistics matching.</p>
                      </div>
                      <div>
                        {deliveryOption === "delivery" && coordinates.lat === null && !isFreeTier ? (
                          <Tag color="orange">Set Location First</Tag>
                        ) : isCalculatingDelivery && deliveryOption === "delivery" ? (
                          <Spin size="small" />
                        ) : isFreeTier ? (
                          <div style={{ textAlign: "right" }}>
                            <Text delete type="secondary" style={{ marginRight: 8 }}>
                              KES {deliveryData?.baseFee ? deliveryData.baseFee.toLocaleString() : "Calculated"}
                            </Text>
                            <Tag color="green" style={{ fontWeight: "bold" }}>FREE DELIVERY</Tag>
                          </div>
                        ) : (
                          <strong>KES {FIXED_DELIVERY_FEE}</strong>
                        )}
                      </div>
                    </div>
                  </Radio>
                </div>

                {/* METHOD B: SELF COLLECT (FREE NO SHIPPING) */}
                <div className={`delivery-option ${deliveryOption === "self_collect" ? "active" : ""}`}>
                  <Radio value="self_collect">
                    <div className="delivery-option-content">
                      <div>
                        <h4>Self-Collection (Free)</h4>
                        <p>Pick up directly at our primary logistics branch warehouse counter.</p>
                      </div>
                      <Tag color="blue">FREE</Tag>
                    </div>
                  </Radio>
                </div>
              </Radio.Group>
            </Card>

            {/* 3. CORE CUSTOMER DETAIL MATRIX */}
            <Card title="3. Customer & Referral Profile" className="checkout-card" style={{ marginTop: "1rem" }}>
              <div className="checkout-form-grid">
                <Input
                  size="large"
                  placeholder="Customer Full Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />

                <div style={{ width: "100%" }}>
                  <PhoneInput
                    country={"ke"}
                    onlyCountries={["ke", "ug", "tz"]}
                    value={phone}
                    onChange={handlePhoneChange}
                    inputStyle={{ width: "100%", height: "50px", borderRadius: "12px" }}
                  />
                  {phoneError && <Text type="danger">{phoneError}</Text>}
                </div>

                <Input
                  size="large"
                  placeholder="Agent Assignment Code (Optional)"
                  value={agentCode}
                  onChange={(e) => setAgentCode(e.target.value)}
                />

                <TextArea
                  rows={3}
                  placeholder="Add optional delivery access codes or instructions..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </Card>
          </Col>

          {/* SUMMARY SIDEBAR SUMMARY ACCOUNTABILITY BOX */}
          <Col xs={24} lg={9}>
            <div className="checkout-summary-wrapper">
              <Card className="checkout-summary-card">
                <div className="summary-top">
                  <FaShoppingBag />
                  <Title level={4}>Order Checkout</Title>
                </div>

                <Divider />

                <div className="checkout-items-list">
                  {cartItems.map((item) => (
                    <div key={item.productId} className="checkout-item">
                      <Text>{item.title} x {item.qty}</Text>
                      <strong>KES {(item.displayPrice * item.qty).toLocaleString()}</strong>
                    </div>
                  ))}
                </div>

                <Divider />

                {/* Internal Logistics Insight panel shown on Free Tier for cost mapping clarity */}
                {isFreeTier && deliveryData && deliveryOption !== "self_collect" && coordinates.lat !== null && (
                  <>
                    <div className="logistics-insights-card evaluation-insights">
                      <div className="insight-header">
                        <InfoCircleOutlined /> <span>Internal Value Evaluation ({deliveryData.vehicleName || "Fleet"})</span>
                      </div>
                      <div className="insight-grid">
                        <div className="insight-item">
                          <FaRoute /> <span>{deliveryData.distanceKm?.toFixed(1)} km</span>
                        </div>
                        <div className="insight-item">
                          <FaBalanceScale /> <span>{deliveryData.totalWeightKg?.toFixed(1)} kg</span>
                        </div>
                        <div className="insight-item">
                          <FaRulerCombined /> <span>{deliveryData.totalVolumeM3?.toFixed(3)} m³</span>
                        </div>
                      </div>
                      <div className="internal-cost-badge">
                        Audited Cost Trace: KES {deliveryData.baseFee?.toLocaleString()}
                      </div>
                    </div>
                    <Divider />
                  </>
                )}

                <div className="summary-row">
                  <span>Subtotal</span>
                  <strong>KES {subtotal.toLocaleString()}</strong>
                </div>

                <div className="summary-row" style={{ marginTop: "8px" }}>
                  <span>Fulfillment Logistics Cost</span>
                  <strong>
                    {isCalculatingDelivery ? (
                      <Spin size="small" />
                    ) : deliveryOption !== "self_collect" && coordinates.lat === null && !isFreeTier ? (
                      <span style={{ fontSize: "12px", color: "#faad14" }}>Awaiting Location Pin</span>
                    ) : deliveryFee === 0 ? (
                      <span style={{ color: "#52c41a", fontWeight: "600" }}>FREE</span>
                    ) : (
                      `KES ${deliveryFee.toLocaleString()}`
                    )}
                  </strong>
                </div>

                <Divider />

                <div className="summary-total">
                  <span>Total Amount Due</span>
                  <Title level={3} style={{ color: "#1677ff" }}>
                    KES {total.toLocaleString()}
                  </Title>
                </div>

                <Button
                  type="primary"
                  block
                  icon={<FaMoneyBillWave />}
                  loading={placeOrder.isPending || isCalculatingDelivery}
                  onClick={handlePlaceOrder}
                  style={{ marginTop: "1rem" }}
                >
                  Place Consolidated Order
                </Button>
              </Card>
            </div>
          </Col>
        </Row>

        <SuccessModal
          open={showSuccess}
          onClose={() => setShowSuccess(false)}
          cart={successCart}
          customerDetails={{ name, phone, address, notes }}
        />
      </div>
    </>
  );
};
export default Checkout;